import React, { createContext, useContext, useLayoutEffect, useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { setSurfaceScope, clearSurfaceScope } from '../../services/security/surfaceScope';

/**
 * The surface a route pins, for components that need it WHILE RENDERING: `{ surface,
 * atlasSurface }`, or null outside any <SurfaceScope>. The module-level scope (getSurfaceScope)
 * is set in a layout effect, after the page's first render, so a page that decides what to draw
 * from it would draw the unscoped page first; this context is there on that first render.
 */
export const PinnedSurfaceContext = createContext(null);

/** `{ surface, atlasSurface }` of the enclosing <SurfaceScope>, or null when unpinned. */
export const usePinnedSurface = () => useContext(PinnedSurfaceContext);

/**
 * Pins the shared security views to one surface for everything rendered inside.
 *
 * Wraps a product module's security routes so the Atlas pages — mounted here
 * unchanged — only ever ask about that product. Used as a layout route:
 *
 *   <Route element={<SurfaceScope surface="gemini_enterprise" atlasSurface="gemini-enterprise" />}>
 *     <Route path="incidents" element={<AtlasIncidents />} />
 *   </Route>
 *
 * `surface` is the security plane's name for it (security service, graph, metrics engine);
 * `atlasSurface` the Agent Atlas id, which Atlas's own reads match exactly (see
 * setSurfaceScope). Leave it out where the two are the same.
 *
 * `useLayoutEffect` rather than `useEffect`: children fire their first fetch in
 * their own effects, and layout effects run first, so the scope is in place
 * before any request leaves. With `useEffect` the ordering is the other way
 * round and the first load of every page would be unscoped — which is the
 * failure that shows one product's data under another product's heading.
 */
const SurfaceScope = ({
  surface, atlasSurface = null, emptyOnUnavailable = false, children,
}) => {
  useLayoutEffect(() => {
    setSurfaceScope(surface, { emptyOnUnavailable, atlasSurface });
    return () => clearSurfaceScope();
  }, [surface, atlasSurface, emptyOnUnavailable]);

  const pinned = useMemo(
    () => (surface ? { surface, atlasSurface: atlasSurface || surface } : null),
    [surface, atlasSurface],
  );

  return (
    <PinnedSurfaceContext.Provider value={pinned}>
      {children ?? <Outlet />}
    </PinnedSurfaceContext.Provider>
  );
};

export default SurfaceScope;
